/**
 * Face shape classifier for the forge-tryon eyewear widget.
 *
 * Classifies one of 7 face shapes from MediaPipe's 468 face mesh landmarks
 * using purely geometric ratio-based logic — no additional ML model required.
 *
 * Classification is cached for 30 frames (~0.5 s at 60 fps) to avoid
 * redundant computation on every rAF tick.
 *
 * Key landmark indices used:
 *   10  — forehead top centre
 *   21  — left brow outer (forehead width left)
 *   152 — chin centre (face height bottom)
 *   172 — left jaw angle
 *   175 — left chin (chin width)
 *   200 — right chin (chin width)
 *   251 — right brow outer (forehead width right)
 *   356 — right zygomatic (cheekbone right)
 *   397 — right jaw angle
 *   127 — left zygomatic (cheekbone left)
 */

import type { FaceShape, FaceShapeResult } from './landmarks.js';

// Re-export types for backward compat with direct imports from this module.
export type { FaceShape, FaceShapeResult };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Recompute classification every N frames (~0.5 s at 60 fps). */
const CACHE_FRAMES = 30;

/** Minimum meaningful face height — guards against degenerate inputs. */
const MIN_FACE_HEIGHT = 0.001;

/** Minimum meaningful cheekbone width — guards against degenerate inputs. */
const MIN_CHEEKBONE_WIDTH = 0.001;

/** Frame style recommendations keyed by face shape. */
const FRAME_RECOMMENDATIONS: Record<FaceShape, string[]> = {
  oval: ['Rectangle', 'Square', 'Aviator', 'Cat-eye', 'Wayfarer'],
  round: ['Rectangle', 'Square', 'Angular', 'Geometric', 'Browline'],
  square: ['Round', 'Oval', 'Aviator', 'Semi-rimless', 'Curved'],
  heart: ['Cat-eye', 'Aviator', 'Round', 'Browline', 'Oval'],
  diamond: ['Oval', 'Rimless', 'Cat-eye', 'Browline', 'Semi-rimless'],
  oblong: ['Oversized', 'Wide Rectangle', 'Browline', 'Wrap', 'Round'],
  triangle: ['Cat-eye', 'Aviator', 'Browline', 'Round', 'Semi-rimless'],
};

// ---------------------------------------------------------------------------
// FaceShapeClassifier
// ---------------------------------------------------------------------------

export class FaceShapeClassifier {
  private _cachedResult: FaceShapeResult | null = null;
  private _framesSinceCompute = 0;

  /**
   * Classify face shape from all 468 MediaPipe face mesh landmarks.
   *
   * Returns a cached result if fewer than CACHE_FRAMES have elapsed since the
   * last computation. Returns `null` when:
   *  - Fewer than 468 landmarks are provided (face mesh not fully detected).
   *  - Computed face height or cheekbone width are degenerate (< 0.001).
   */
  classify(allLandmarks: [number, number, number][]): FaceShapeResult | null {
    if (allLandmarks.length < 468) return this._cachedResult;

    this._framesSinceCompute++;
    if (this._cachedResult !== null && this._framesSinceCompute < CACHE_FRAMES) {
      return this._cachedResult;
    }
    this._framesSinceCompute = 0;

    // ── Measurements ─────────────────────────────────────────────────────────

    const faceHeight      = this._distance(allLandmarks[10]!,  allLandmarks[152]!);
    const foreheadWidth   = this._distance(allLandmarks[21]!,  allLandmarks[251]!);
    const cheekboneWidth  = this._distance(allLandmarks[127]!, allLandmarks[356]!);
    const jawWidth        = this._distance(allLandmarks[172]!, allLandmarks[397]!);

    if (faceHeight < MIN_FACE_HEIGHT || cheekboneWidth < MIN_CHEEKBONE_WIDTH) {
      return null;
    }

    const widthHeightRatio = cheekboneWidth / faceHeight;
    const foreheadRatio    = foreheadWidth  / cheekboneWidth;
    const jawRatio         = jawWidth       / cheekboneWidth;

    // ── Classification ────────────────────────────────────────────────────────

    const shape      = this._classifyFromRatios(widthHeightRatio, foreheadRatio, jawRatio);
    const confidence = this._computeConfidence(widthHeightRatio, foreheadRatio, jawRatio, shape);
    const recommended = FRAME_RECOMMENDATIONS[shape] ?? [];

    this._cachedResult = {
      shape,
      confidence,
      measurements: {
        faceHeight,
        foreheadWidth,
        cheekboneWidth,
        jawWidth,
        widthHeightRatio,
        foreheadRatio,
        jawRatio,
      },
      recommended,
    };

    return this._cachedResult;
  }

  /** Clear the cached result and reset the frame counter. */
  reset(): void {
    this._cachedResult = null;
    this._framesSinceCompute = 0;
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  /**
   * Rule-based classification from the three core ratios.
   *
   * Ratios:
   *   wh — widthHeightRatio: cheekboneWidth / faceHeight
   *          High  → face is wide relative to height (round, square)
   *          Low   → face is tall/narrow (oblong)
   *   fr — foreheadRatio: foreheadWidth / cheekboneWidth
   *          High  → forehead as wide as cheekbones (round, square)
   *          Low   → narrow forehead (diamond, triangle)
   *   jr — jawRatio: jawWidth / cheekboneWidth
   *          High  → wide jaw relative to cheekbones (square, triangle)
   *          Low   → narrow jaw (heart, diamond)
   */
  private _classifyFromRatios(wh: number, fr: number, jr: number): FaceShape {
    // Round: nearly circular, all widths similar, wide relative to height
    if (wh > 0.85 && fr > 0.85 && jr > 0.85) return 'round';

    // Square: equal widths, wide jaw, moderate height
    if (wh > 0.75 && fr > 0.85 && jr > 0.85) return 'square';

    // Oblong: very tall relative to width, proportional horizontal widths
    if (wh < 0.60 && fr > 0.80 && jr > 0.80) return 'oblong';

    // Heart: wide forehead, narrow jaw, pointed chin
    if (fr > 0.95 && jr < 0.80) return 'heart';

    // Diamond: cheekbones widest, narrow forehead and jaw
    if (fr < 0.85 && jr < 0.85) return 'diamond';

    // Triangle: jaw wider than forehead (pear-shaped)
    if (jr > 1.0 && fr < 0.85) return 'triangle';

    // Oval: balanced proportions — the catch-all default
    return 'oval';
  }

  /**
   * Compute classification confidence in [0.3, 1.0].
   *
   * Confidence reflects how clearly the measurements match the classified
   * shape's criteria — how far from the nearest classification boundary.
   */
  private _computeConfidence(wh: number, fr: number, jr: number, shape: FaceShape): number {
    let score: number;

    switch (shape) {
      case 'round':
        // Strongly round: wh and fr both well above 0.85
        score = 0.5 + (wh - 0.85) * 3 + (fr - 0.85) * 2;
        break;
      case 'square':
        // Strong square: jaw wide AND face moderately wide
        score = 0.5 + (jr - 0.85) * 3 + (wh - 0.75) * 2;
        break;
      case 'oblong':
        // Strong oblong: very low wh (tall and narrow)
        score = 0.5 + (0.60 - wh) * 4;
        break;
      case 'heart':
        // Strong heart: forehead clearly wider than jaw
        score = 0.5 + (fr - 0.95) * 5 + (0.80 - jr) * 3;
        break;
      case 'diamond':
        // Strong diamond: both forehead and jaw clearly narrower than cheekbones
        score = 0.5 + (0.85 - fr) * 3 + (0.85 - jr) * 3;
        break;
      case 'triangle':
        // Strong triangle: jaw clearly wider than cheekbones
        score = 0.5 + (jr - 1.0) * 3;
        break;
      case 'oval':
      default:
        // Oval is the default — moderate baseline confidence
        score = 0.75;
        break;
    }

    // Clamp to valid range
    return Math.max(0.3, Math.min(1.0, score));
  }

  /** Euclidean distance between two 3-D points. */
  private _distance(
    a: [number, number, number],
    b: [number, number, number],
  ): number {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const dz = a[2] - b[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
}
